import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useUserToken } from "@/context/auth-context";
import { useWorkspaceMembers } from "@/queries";
import { useActiveOrgMembers } from "@/queries/organization-members";
import { useWorkspaceInvites } from "@/queries/workspace-invites";
import { WorkspaceInviteFormValues } from "@/schemas/workspace-invite";
import { WorkspaceMember } from "@/types/workspaceMembers";
import { AlertCircle, ChevronsUpDown, SearchIcon, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

/**
 * SelectEmails component for workspace invite form
 *
 * Features:
 * - Displays organization members for selection
 * - Allows manual email entry for external users
 * - Prevents duplicate invites by filtering out already invited users
 * - Shows visual feedback for already invited emails
 * - Displays selected emails as removable tags
 * - Real-time validation of email addresses
 */
const SelectEmails = () => {
  const form = useFormContext<WorkspaceInviteFormValues>();
  const emails = Array.from(form.watch("emails") ?? []);
  const workspaceId = form.watch("workspace_id");
  const orgMembersQuery = useActiveOrgMembers();
  const [newEmail, setNewEmail] = useState("");

  // Get pending invites for the selected workspace
  const workspaceInvitesQuery = useWorkspaceInvites(
    workspaceId ? { workspace_id: workspaceId, status: "pending" } : undefined,
  );

  const userToken = useUserToken();

  const workspaceMembersQuery = useWorkspaceMembers(userToken, workspaceId);

  const workspaceMembers: WorkspaceMember[] = useMemo(
    () => workspaceMembersQuery?.data?.workspace_members || [],
    [workspaceMembersQuery],
  );

  // Create a set of already invited emails for quick lookup
  const invitedEmails = useMemo(() => {
    const pendingInvites = workspaceInvitesQuery.data?.items || [];
    return new Set(
      pendingInvites.map((invite) => invite.invitee_email.toLowerCase()),
    );
  }, [workspaceInvitesQuery.data?.items]);

  // Filter out already invited members
  const availableMembers = useMemo(() => {
    const members = orgMembersQuery.data?.items || [];
    return members.filter(
      (member) =>
        !workspaceMembers.find(({ user_id }) => user_id === member.user_id) &&
        !invitedEmails.has(member.user.email.toLowerCase()),
    );
  }, [orgMembersQuery.data?.items, workspaceMembers, invitedEmails]);

  // Check if the new email being typed is already invited
  const isNewEmailAlreadyInvited =
    newEmail && workspaceId && invitedEmails.has(newEmail.toLowerCase());

  return (
    <FormField
      control={form.control}
      name="emails"
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>
            Emails <span className="text-red-500">*</span>
          </FormLabel>
          <FormControl>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="h-auto min-h-[40px] justify-between"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <SearchIcon className="h-4 flex-shrink-0 text-gray-400" />
                    {emails.length > 0 ? (
                      <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                        {emails.map((email, index) => (
                          <div
                            key={index}
                            className="inline-flex max-w-full items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-sm text-blue-800"
                          >
                            <span className="truncate">{email}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                field.onChange(
                                  emails.filter((_, i) => i !== index),
                                );
                              }}
                              className="flex-shrink-0 rounded-sm p-0.5 hover:bg-blue-200"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500">Select emails</span>
                    )}
                  </div>
                  <ChevronsUpDown className="w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0"
                align="start"
                style={{ width: "var(--radix-popover-trigger-width)" }}
              >
                <Command>
                  <CommandInput
                    placeholder="Search members or enter email..."
                    value={newEmail}
                    onValueChange={setNewEmail}
                  />
                  <CommandList>
                    <CommandEmpty>No pre-existing members found.</CommandEmpty>
                    <CommandGroup>
                      {availableMembers.map((member) => (
                        <CommandItem
                          key={member.user_id}
                          value={member.user.email}
                          onSelect={(currentValue) => {
                            field.onChange(
                              emails.some((email) => email === currentValue)
                                ? emails.filter(
                                    (email) => email !== currentValue,
                                  )
                                : [...emails, currentValue],
                            );
                          }}
                        >
                          <Checkbox
                            checked={emails.some(
                              (email) => email === member.user.email,
                            )}
                            className="mr-1.5"
                          />
                          <div>{member.user.email}</div>
                        </CommandItem>
                      ))}
                      {newEmail && !isNewEmailAlreadyInvited ? (
                        <CommandItem
                          className="cursor-default select-none"
                          onSelect={() => {
                            if (newEmail.trim()) {
                              field.onChange([...emails, newEmail.trim()]);
                              setNewEmail("");
                            }
                          }}
                        >
                          <Checkbox checked={false} className="mr-1.5" />
                          <div>Add "{newEmail}"</div>
                        </CommandItem>
                      ) : null}
                      {isNewEmailAlreadyInvited ? (
                        <CommandItem
                          className="cursor-default select-none opacity-50"
                          disabled
                        >
                          <div className="size-4.5 pointer-events-none shrink-0 select-none rounded-[4px] border border-input">
                            <AlertCircle className="size-3.5 text-orange-500" />
                          </div>
                          <div className="ml-1.5 text-orange-600">
                            "{newEmail}" is already invited
                          </div>
                        </CommandItem>
                      ) : null}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default SelectEmails;
